import {styled} from "@mui/material/styles";
import {type MouseEvent, type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";

const ELLIPSIS_TEXT = "... ";
const HEIGHT_TOLERANCE_PX = 1;
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

type GraphemeSegmenter = {
    segment(text: string): Iterable<{segment: string}>;
};

type GraphemeSegmenterConstructor = new (
    locales?: string | string[],
    options?: {granularity: "grapheme"},
) => GraphemeSegmenter;

let graphemeSegmenter: GraphemeSegmenter | undefined;

const Root = styled("span")({
    display: "block",
    position: "relative",
});

const Measure = styled("span")({
    position: "absolute",
    top: 0,
    left: 0,
    visibility: "hidden",
    pointerEvents: "none",
    overflowWrap: "break-word",
});

const Toggle = styled("button")(({theme}) => ({
    appearance: "none",
    border: 0,
    padding: 0,
    background: "none",
    color: theme.palette.text.secondary,
    cursor: "pointer",
    font: "inherit",
    lineHeight: "inherit",
    verticalAlign: "baseline",
    WebkitTapHighlightColor: "transparent",
    "&:hover": {
        textDecoration: "underline",
    },
}));

function trimAtWordBoundary(text: string) {
    const trimmedText = text.trimEnd();
    const lastSpaceIndex = trimmedText.lastIndexOf(" ");
    if (lastSpaceIndex > 0 && lastSpaceIndex >= trimmedText.length - 20) {
        return trimmedText.slice(0, lastSpaceIndex);
    }
    return trimmedText;
}

function splitGraphemes(text: string) {
    const Segmenter =
        typeof Intl === "undefined"
            ? undefined
            : (Intl as typeof Intl & {Segmenter?: GraphemeSegmenterConstructor}).Segmenter;
    if (typeof Segmenter === "function") {
        graphemeSegmenter ??= new Segmenter(undefined, {granularity: "grapheme"});
        return Array.from(graphemeSegmenter.segment(text), (segment) => segment.segment);
    }
    return Array.from(text);
}

export interface ProjectCardExpandCollapseProps {
    text: string;
    previewHeight: number;
    expanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    showToggle?: boolean;
    expandText?: string;
    collapseText?: string;
    forceToggle?: boolean;
    onTruncatedChange?: (truncated: boolean) => void;
    renderText?: (text: string) => ReactNode;
}

export function ProjectCardExpandCollapse({
    text,
    previewHeight,
    expanded: expandedProp,
    onExpandedChange,
    showToggle = true,
    expandText = "show more",
    collapseText = "show less",
    forceToggle = false,
    onTruncatedChange,
    renderText = (value) => value,
}: ProjectCardExpandCollapseProps) {
    const rootRef = useRef<HTMLSpanElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const [internalExpanded, setInternalExpanded] = useState(false);
    const [collapsedText, setCollapsedText] = useState(text);
    const [truncated, setTruncated] = useState(false);
    const [textAndToggleFits, setTextAndToggleFits] = useState(false);
    const lastReportedTruncated = useRef<boolean>();
    const collapseRequested = useRef(false);
    const isControlled = expandedProp !== undefined;
    const expanded = isControlled ? expandedProp : internalExpanded;

    const measure = useCallback(() => {
        const root = rootRef.current;
        const measureElement = measureRef.current;
        if (!root || !measureElement || root.clientWidth === 0) {
            return;
        }

        measureElement.style.width = `${root.clientWidth}px`;
        measureElement.textContent = text;
        const textIsTruncated = measureElement.scrollHeight > previewHeight + HEIGHT_TOLERANCE_PX;
        setTruncated(textIsTruncated);
        if (lastReportedTruncated.current !== textIsTruncated) {
            lastReportedTruncated.current = textIsTruncated;
            onTruncatedChange?.(textIsTruncated);
        }

        const needsToggle = showToggle && (textIsTruncated || forceToggle);
        const shouldTruncateWithoutToggle = !showToggle && textIsTruncated;
        if (expanded && showToggle && !needsToggle) {
            if (!collapseRequested.current) {
                collapseRequested.current = true;
                if (isControlled) {
                    onExpandedChange?.(false);
                } else {
                    setInternalExpanded(false);
                    onExpandedChange?.(false);
                }
            }
        } else {
            collapseRequested.current = false;
        }

        if (!needsToggle && !shouldTruncateWithoutToggle) {
            setCollapsedText(text);
            setTextAndToggleFits(false);
            return;
        }

        if (showToggle) {
            measureElement.textContent = `${text} ${expandText}`;
            const fullTextAndToggleFits = measureElement.scrollHeight <= previewHeight + HEIGHT_TOLERANCE_PX;
            setTextAndToggleFits(fullTextAndToggleFits);
            if (fullTextAndToggleFits) {
                setCollapsedText(text);
                return;
            }
        } else {
            setTextAndToggleFits(false);
        }

        const characters = splitGraphemes(text);
        let lowerBound = 0;
        let upperBound = characters.length;

        while (lowerBound < upperBound) {
            const candidateLength = Math.ceil((lowerBound + upperBound) / 2);
            const candidate = characters.slice(0, candidateLength).join("").trimEnd();
            measureElement.textContent = showToggle
                ? `${candidate}${ELLIPSIS_TEXT}${expandText}`
                : `${candidate}${ELLIPSIS_TEXT}`;

            if (measureElement.scrollHeight <= previewHeight + HEIGHT_TOLERANCE_PX) {
                lowerBound = candidateLength;
            } else {
                upperBound = candidateLength - 1;
            }
        }

        const fittedText = characters.slice(0, lowerBound).join("");
        setCollapsedText(showToggle ? trimAtWordBoundary(fittedText) : fittedText.trimEnd());
    }, [
        expanded,
        expandText,
        forceToggle,
        isControlled,
        onExpandedChange,
        onTruncatedChange,
        previewHeight,
        showToggle,
        text,
    ]);

    useIsomorphicLayoutEffect(() => {
        measure();

        const root = rootRef.current;
        if (!root) {
            return;
        }

        const resizeObserver = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(measure);
        resizeObserver?.observe(root);

        let cancelled = false;
        void document.fonts?.ready.then(() => {
            if (!cancelled) {
                measure();
            }
        });

        return () => {
            cancelled = true;
            resizeObserver?.disconnect();
        };
    }, [measure]);

    const toggleExpanded = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            const nextExpanded = !expanded;
            if (!isControlled) {
                setInternalExpanded(nextExpanded);
            }
            onExpandedChange?.(nextExpanded);
        },
        [expanded, isControlled, onExpandedChange],
    );

    const needsToggle = showToggle && (truncated || forceToggle);
    const shouldShowCollapsedText = needsToggle || (!showToggle && truncated);
    return (
        <Root ref={rootRef}>
            <Measure ref={measureRef} aria-hidden />
            {expanded ? (
                <>
                    {renderText(text)}
                    {showToggle && (
                        <>
                            {" "}
                            <Toggle type="button" onClick={toggleExpanded} aria-expanded>
                                {collapseText}
                            </Toggle>
                        </>
                    )}
                </>
            ) : needsToggle && textAndToggleFits ? (
                <>
                    {renderText(text)}{" "}
                    <Toggle type="button" onClick={toggleExpanded} aria-expanded={false}>
                        {expandText}
                    </Toggle>
                </>
            ) : shouldShowCollapsedText ? (
                <>
                    {renderText(collapsedText)}
                    {ELLIPSIS_TEXT}
                    {showToggle && (
                        <Toggle type="button" onClick={toggleExpanded} aria-expanded={false}>
                            {expandText}
                        </Toggle>
                    )}
                </>
            ) : (
                renderText(text)
            )}
        </Root>
    );
}
